import type { ReactNode } from 'react';
import { ComponentHost } from '../../runtime/ComponentHost';
import { useDeclaredComponent } from '../../runtime/useDeclaredComponent';
import type { CardSlot } from '../assembly';
import { CardZonesView, defaultZones, type CardZones } from './CardZones';
import type { RendererProps } from './TableRenderer';
import type { Row } from './useCollection';

/**
 * Renderer du composant Core `CardList` (59 §5.3 « Collection renderer » ; ADR-096, ADR-099,
 * ADR-100) : même requête, même sélection que la table, sur le fond de page ; chaque carte est un
 * **modèle JSON par zones** (la même définition que les cartes du Kanban) ou un composite publié
 * qui reçoit l'enregistrement. Le titre est le bouton d'ouverture, étendu à toute la carte ; la
 * case de sélection reste au-dessus.
 */
export function CardRenderer({ rows, object, columns, render, selectedId, onOpen, selection, card }: RendererProps & { card: CardSlot | undefined }) {
  const zones = card !== undefined && 'zones' in card ? (card as CardZones) : undefined;
  const composite = card !== undefined && 'component' in card ? card : undefined;
  const minWidth = zones?.min_width ?? 300;
  return (
    <ul className="ay-cards" aria-label="Cartes" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${minWidth}px, 100%), 1fr))` }}>
      {rows.map((row, index) => {
        const id = typeof row.id === 'string' ? row.id : String(index);
        const checked = selection?.has(id) ?? false;
        const className = `ay-cards__item${selectedId === id ? ' ay-cards__item--selected' : ''}${checked ? ' ay-cards__item--checked' : ''}${composite === undefined ? '' : ' ay-cards__item--composite'}`;
        const check = (title: string): ReactNode =>
          selection !== undefined && <input type="checkbox" className="ay-cards__check" aria-label={`Sélectionner ${title}`} checked={checked} onChange={() => undefined} onClick={(event) => selection.pick(id, event)} />;
        if (composite !== undefined) {
          return (
            <li key={id} data-opens-record className={className}>
              <CompositeCard artifactId={composite.component} row={row} {...(composite.inputs === undefined ? {} : { inputs: composite.inputs })} />
              <div className="ay-cards__foot">
                <button type="button" className="ay-cards__open ay-cards__open--link" onClick={() => onOpen(row)} aria-current={selectedId === id ? 'true' : undefined}>
                  Ouvrir
                </button>
                {check(`l’élément ${index + 1}`)}
              </div>
            </li>
          );
        }
        const shown = zones?.zones ?? defaultZones(columns);
        const firstTitle = shown[0]?.type === 'identity' ? render.display(shown[0].title_field, row) : `l’élément ${index + 1}`;
        return (
          <li key={id} data-opens-record className={className}>
            {check(firstTitle)}
            <CardZonesView
              zones={shown}
              row={row}
              object={object}
              columns={columns}
              render={render}
              renderTitle={(title) => (
                <button type="button" className="ay-cards__open" onClick={() => onOpen(row)} aria-current={selectedId === id ? 'true' : undefined}>
                  {title}
                </button>
              )}
            />
          </li>
        );
      })}
    </ul>
  );
}

/** Carte composite : le composant publié reçoit l'enregistrement (`$record`) et les entrées de l'emplacement. */
function CompositeCard({ artifactId, row, inputs }: { artifactId: string; row: Row; inputs?: Readonly<Record<string, unknown>> }) {
  const instance = useDeclaredComponent(artifactId, row, inputs);
  if (instance === null) return <p className="ay-text ay-text--muted">Carte indisponible : {artifactId}</p>;
  return <ComponentHost instance={instance} />;
}
